import { useState } from 'react';
import { api, type RelatorioNome } from '../lib/api';
import { useFetch } from '../lib/useFetch';
import { fmtMoney } from '../lib/format';
import type { PageProps } from '../App';

const TABS: { id: RelatorioNome; label: string }[] = [
  { id: 'resumo-despesas', label: 'Resumo despesas' },
  { id: 'resumo-despesas-mes', label: 'Despesas do mês' },
  { id: 'resumo-receitas', label: 'Receitas' },
  { id: 'resumo-geral', label: 'Resumo geral' },
  { id: 'conciliacao-bancaria', label: 'Conciliação' },
  { id: 'dinamica', label: 'Dinâmica' },
  { id: 'retencoes', label: 'Retenções' },
];

interface Col { label: string; right?: boolean }
interface Cell { v: string; right?: boolean; bold?: boolean; indent?: boolean; muted?: boolean; mono?: boolean }
interface Table { cols: Col[]; rows: Cell[][]; nota?: string }

const compMes = (m: string) => { const x = m.match(/^(\d{4})-(\d{2})/); return x ? `${x[2]}/${x[1]}` : m; };

function buildTable(nome: RelatorioNome, data: any): Table {
  const money = (n: number) => fmtMoney(n);
  switch (nome) {
    case 'resumo-despesas': {
      const total = data.total_geral ?? 0;
      const rows: Cell[][] = [];
      (data.classes ?? []).forEach((cl: any) => {
        rows.push([{ v: cl.classe, bold: true }, { v: money(cl.total), right: true, bold: true, mono: true },
          { v: total ? `${(cl.total / total * 100).toFixed(1)}%` : '—', right: true, muted: true, mono: true }]);
        (data.detalhe ?? []).filter((d: any) => d.classe === cl.classe).forEach((d: any) => {
          rows.push([{ v: d.departamento, indent: true, muted: true }, { v: money(d.total), right: true, mono: true },
            { v: '', right: true }]);
        });
      });
      if (data.sem_classificacao?.total) rows.push([{ v: 'Sem classificação', muted: true }, { v: money(data.sem_classificacao.total), right: true, mono: true }, { v: '', right: true }]);
      rows.push([{ v: 'TOTAL GERAL', bold: true }, { v: money(total), right: true, bold: true, mono: true }, { v: '100%', right: true, muted: true, mono: true }]);
      return { cols: [{ label: 'Classe / Departamento' }, { label: 'Total', right: true }, { label: '%', right: true }], rows };
    }
    case 'resumo-despesas-mes': {
      const rows = (data.items ?? []).map((r: any) => [{ v: r.classe, bold: true }, { v: money(r.total), right: true, mono: true }]);
      rows.push([{ v: 'TOTAL', bold: true }, { v: money(data.total_geral ?? 0), right: true, bold: true, mono: true }]);
      return { cols: [{ label: 'Classe' }, { label: 'Total', right: true }], rows };
    }
    case 'resumo-receitas': {
      const rows = (data.items ?? []).map((r: any) => [{ v: compMes(r.mes) }, { v: String(r.recebimentos), right: true, muted: true }, { v: money(r.total), right: true, mono: true }]);
      rows.push([{ v: 'TOTAL', bold: true }, { v: '', right: true }, { v: money(data.total_geral ?? 0), right: true, bold: true, mono: true }]);
      return { cols: [{ label: 'Mês' }, { label: 'Recebimentos', right: true }, { label: 'Total', right: true }], rows };
    }
    case 'resumo-geral': {
      const rows: Cell[][] = [];
      rows.push([{ v: 'ENTRADAS', bold: true }, { v: '', right: true }]);
      rows.push([{ v: 'Receita de vendas', indent: true }, { v: money(data.entradas?.receita_vendas ?? 0), right: true, mono: true }]);
      (data.entradas?.aportes_por_socio ?? []).forEach((a: any) => rows.push([{ v: `Aportes ${a.socio}`, indent: true }, { v: money(a.total), right: true, mono: true }]));
      rows.push([{ v: 'Total entradas', bold: true, indent: true }, { v: money(data.entradas?.total ?? 0), right: true, bold: true, mono: true }]);
      rows.push([{ v: 'SAÍDAS', bold: true }, { v: '', right: true }]);
      (data.saidas?.por_classe ?? []).forEach((s: any) => rows.push([{ v: s.classe, indent: true }, { v: money(s.total), right: true, mono: true }]));
      rows.push([{ v: 'Total saídas', bold: true, indent: true }, { v: money(data.saidas?.total ?? 0), right: true, bold: true, mono: true }]);
      rows.push([{ v: 'DISPONIBILIDADES', bold: true }, { v: '', right: true }]);
      (data.disponibilidades ?? []).forEach((d: any) => rows.push([{ v: d.apelido, indent: true }, { v: money(d.saldo), right: true, mono: true }]));
      return { cols: [{ label: 'Item' }, { label: 'Valor', right: true }], rows };
    }
    case 'conciliacao-bancaria': {
      const rows = (data.items ?? []).map((r: any) => [
        { v: compMes(r.mes) }, { v: money(r.entradas), right: true, mono: true }, { v: money(r.saidas), right: true, mono: true },
        { v: money(r.saldo_acumulado), right: true, mono: true, bold: true },
        { v: money(r.diferenca_vs_sistema), right: true, mono: true, muted: Math.abs(r.diferenca_vs_sistema) < 0.01 },
      ]);
      return { cols: [{ label: 'Mês' }, { label: 'Entradas', right: true }, { label: 'Saídas', right: true }, { label: 'Saldo acum.', right: true }, { label: 'Dif. vs sistema', right: true }], rows };
    }
    case 'dinamica': {
      const items = data.items ?? [];
      const cols: Col[] = [{ label: 'Classe' }, { label: 'Departamento' }, { label: 'Mês' }, { label: 'Total', right: true }];
      const rows = items.map((r: any) => [{ v: r.classe ?? '—', bold: true }, { v: r.departamento ?? '—' }, { v: r.mes ? compMes(r.mes) : '—', muted: true }, { v: money(r.total ?? 0), right: true, mono: true }]);
      return { cols, rows };
    }
    case 'retencoes': {
      const rows = (data.items ?? []).map((r: any) => [
        { v: compMes(r.competencia) }, { v: r.imposto, bold: true }, { v: money(r.valor_guia), right: true, mono: true },
        { v: r.status === 'pago' ? 'Paga' : 'Em aberto', muted: r.status === 'pago' },
      ]);
      return { cols: [{ label: 'Competência' }, { label: 'Imposto' }, { label: 'Valor', right: true }, { label: 'Status' }], rows };
    }
  }
}

export function Relatorios(_: PageProps) {
  const [tab, setTab] = useState<RelatorioNome>('resumo-despesas');
  const [de, setDe] = useState('');
  const [ate, setAte] = useState('');
  const [ano, setAno] = useState(new Date().getFullYear());
  const [contaBanc, setContaBanc] = useState<number | ''>('');

  const bancos = useFetch(() => api.listContasBancarias(), []);
  const precisaBanco = tab === 'conciliacao-bancaria';

  const rep = useFetch(
    () => api.relatorio<any>(tab, {
      de: de || undefined, ate: ate || undefined,
      ...(precisaBanco ? { conta_bancaria_id: contaBanc ? Number(contaBanc) : undefined, ano } : {}),
    }),
    [tab, de, ate, ano, contaBanc],
  );

  const table = rep.data && !(precisaBanco && !contaBanc) ? buildTable(tab, rep.data) : null;
  const gridCols = table ? table.cols.map((c, i) => (i === 0 ? 'minmax(180px,1.6fr)' : c.right ? '130px' : 'minmax(120px,1fr)')).join(' ') : '';

  const csvUrl = api.relatorioCsvUrl(tab, { de: de || undefined, ate: ate || undefined, ...(precisaBanco ? { conta_bancaria_id: contaBanc ? Number(contaBanc) : undefined, ano } : {}) });

  return (
    <div className="page mid">
      <div className="page-head">
        <h1>Relatórios</h1>
        <div className="row" style={{ gap: 6 }}>
          <input type="date" className="input sm mono" style={{ width: 'auto' }} value={de} onChange={(e) => setDe(e.target.value)} />
          <span className="text-sm muted">–</span>
          <input type="date" className="input sm mono" style={{ width: 'auto' }} value={ate} onChange={(e) => setAte(e.target.value)} />
        </div>
        <a className="btn ghost green" href={csvUrl}>↓ Exportar CSV</a>
      </div>

      <div className="tabs">
        {TABS.map((t) => <button key={t.id} className={`tab ${tab === t.id ? 'active' : ''}`} onClick={() => setTab(t.id)}>{t.label}</button>)}
      </div>

      {precisaBanco && (
        <div className="row" style={{ gap: 10, marginBottom: 14 }}>
          <select className="select sm" style={{ width: 'auto' }} value={contaBanc} onChange={(e) => setContaBanc(e.target.value ? Number(e.target.value) : '')}>
            <option value="">Escolher conta bancária…</option>
            {(bancos.data?.items ?? []).map((b) => <option key={b.id} value={b.id}>{b.apelido}</option>)}
          </select>
          <input type="number" className="input sm mono" style={{ width: 100 }} value={ano} onChange={(e) => setAno(Number(e.target.value))} />
        </div>
      )}

      {rep.loading ? (
        <div className="loading">Carregando…</div>
      ) : precisaBanco && !contaBanc ? (
        <div className="empty">Escolha uma conta bancária para ver a conciliação.</div>
      ) : !table || table.rows.length === 0 ? (
        <div className="empty">Sem dados no período.</div>
      ) : (
        <div className="tbl tbl-scroll">
          <div style={{ minWidth: table.cols.length * 140 }}>
            <div className="tbl-head" style={{ gridTemplateColumns: gridCols }}>
              {table.cols.map((c, i) => <span key={i} className={c.right ? 'cell-r' : ''}>{c.label}</span>)}
            </div>
            {table.rows.map((row, ri) => (
              <div className="tbl-row" key={ri} style={{ gridTemplateColumns: gridCols }}>
                {row.map((cell, ci) => (
                  <span key={ci} className={[cell.right ? 'cell-r' : '', cell.bold ? 'cell-strong' : '', cell.muted ? 'muted' : '', cell.mono ? 'mono' : ''].join(' ')}
                    style={{ paddingLeft: cell.indent ? 18 : undefined, fontSize: 13 }}>{cell.v}</span>
                ))}
              </div>
            ))}
          </div>
        </div>
      )}
      <div className="tbl-note">CSV baixa com BOM para abrir no Excel com acentos corretos.</div>
    </div>
  );
}
